import React, { useEffect, useState } from 'react';
import { useToast } from '../Contexts/ToastContext';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  normalizeNotificationPreferences,
  syncNotificationPreferencesFromProfile,
  updateNotificationPreferences,
} from '../utils/notificationPreferences';

function SettingsToggle({ checked, disabled, onChange, label }) {
  return (
    <label className="settings-toggle" aria-label={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="settings-toggle__track" aria-hidden="true" />
    </label>
  );
}

export function NotificationSettingsSection({ profileData, isGuest, onPreferencesChange, onSaved }) {
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState(DEFAULT_NOTIFICATION_PREFERENCES);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    const fromProfile = profileData?.data?.notificationPreferences;
    if (fromProfile) {
      const normalized = syncNotificationPreferencesFromProfile(fromProfile);
      setPrefs(normalized);
      onPreferencesChange?.(normalized);
    }
  }, [profileData?.data?.notificationPreferences, onPreferencesChange]);

  const handleToggle = async (key, nextValue) => {
    if (isGuest) {
      showToast({ message: 'Create an account to manage notification settings.' });
      return;
    }

    const previous = prefs;
    const optimistic = { ...prefs, [key]: nextValue };
    setPrefs(optimistic);
    setSavingKey(key);

    try {
      const updated = await updateNotificationPreferences({ [key]: nextValue });
      setPrefs(updated);
      syncNotificationPreferencesFromProfile(updated);
      onPreferencesChange?.(updated);
      onSaved?.(updated);
    } catch (err) {
      setPrefs(previous);
      const msg = err.response?.data?.msg || err.response?.data?.message;
      showToast({ message: msg || 'Could not update notification settings.' });
    } finally {
      setSavingKey(null);
    }
  };

  const hasPhone = Boolean(profileData?.data?.phone);
  const normalized = normalizeNotificationPreferences(prefs);

  return (
    <div className="settings-section">
      <h4 className="settings-section-title">Notifications</h4>
      <div className="settings-row">
        <span><i className="bi bi-bell" /> Push notifications</span>
        <span className="settings-value">On</span>
      </div>
      <div className="settings-row">
        <span><i className="bi bi-envelope" /> Email (orders, promotions)</span>
        <span className="settings-value">On</span>
      </div>
      <div className="settings-row">
        <span><i className="bi bi-chat-dots" /> SMS notifications</span>
        <SettingsToggle
          label="SMS notifications"
          checked={normalized.sms}
          disabled={isGuest || !hasPhone || savingKey === 'sms'}
          onChange={(value) => handleToggle('sms', value)}
        />
      </div>
      <div className="settings-row">
        <span><i className="bi bi-whatsapp" /> WhatsApp notifications</span>
        <SettingsToggle
          label="WhatsApp notifications"
          checked={normalized.whatsapp}
          disabled={isGuest || !hasPhone || savingKey === 'whatsapp'}
          onChange={(value) => handleToggle('whatsapp', value)}
        />
      </div>
      {!hasPhone && !isGuest && (
        <p className="settings-hint">Add a mobile number to your profile to enable SMS and WhatsApp alerts.</p>
      )}
    </div>
  );
}
